/**
 * useBookingPricing — one price for every booker, member or guest.
 *
 * The booking flow has two pricing endpoints because it has two auth states:
 * `/bookings/validate` needs a bearer token, and the guest flow has none until
 * the account is created on the last step. This hook is the seam, so the steps
 * that render a price never have to know which one they are on — and, more to
 * the point, never have to keep a browser-side fallback for the case where they
 * cannot ask.
 *
 * **The client computes nothing.** A per-bay premium lives on the bay, and while
 * the club payload carries its rate, whether this booker owes it is decided on the
 * server — so any local arithmetic is wrong by exactly that amount. Everything
 * here comes off the wire.
 *
 * The two payloads differ: the guest quote carries no `usage`, `subscription` or
 * `overage`, because an anonymous caller holds no plan. Fields that only exist on
 * the member payload are `undefined` on the guest path rather than faked.
 *
 * @module booking/hooks
 */

import { useAuth } from '~/services/auth';
import { useBookingValidation } from './useBookingValidation';
import { useBookingQuote } from './useBookingQuote';

export interface UseBookingPricingOptions {
  clubId: number | null | undefined;
  bayId: number | null | undefined;
  /** ISO 8601 UTC */
  startTime: string | null | undefined;
  /** ISO 8601 UTC */
  endTime: string | null | undefined;
  /** Guests excluding the booker. */
  guestCount?: number;
  /** The member path also needs the user id for usage math. */
  userId?: number | null;
  /**
   * A promo code, from a link or typed at checkout. Sent on BOTH paths: the
   * guest quote prices it loosely (the three per-user rules are skipped) and
   * the member quote strictly, which is the whole reason a returning member
   * learns their code is spent before they reach the card.
   */
  promoCode?: string;
  enabled?: boolean;
}

export function useBookingPricing(options: UseBookingPricingOptions) {
  const { isAuthenticated } = useAuth();
  const { clubId, bayId, startTime, endTime, guestCount, userId, promoCode, enabled = true } = options;

  const hasRequiredParams = !!clubId && !!bayId && !!startTime && !!endTime;
  const isEnabled = enabled && hasRequiredParams;

  // Exactly one of these runs. Both are unconditionally *called* — hooks cannot be
  // conditional — but the inactive one is disabled, so it issues no request and
  // holds no cache entry.
  const member = useBookingValidation({
    clubId, bayId, startTime, endTime, guestCount, userId, promoCode,
    enabled: isEnabled && isAuthenticated,
  });
  const guest = useBookingQuote({
    clubId, bayId, startTime, endTime, guestCount, promoCode,
    enabled: isEnabled && !isAuthenticated,
  });

  const active = isAuthenticated ? member : guest;
  const data = active.data ?? null;

  return {
    /** The priced breakdown, in render order. */
    fees: data?.fees ?? [],
    totalCost: data?.totalCost,
    /**
     * How `totalCost` divides when the booking has more than one payer — a team
     * booking whose overage the leads pay. Absent when it has one. Decide the
     * card prompt from `payerBreakdown.booker`, not from `totalCost`.
     */
    payerBreakdown: data?.payerBreakdown,
    isValid: data?.isValid,
    errors: data?.errors ?? [],
    canProceedWithPayment: data?.canProceedWithPayment,
    bayCapacity: data?.bayCapacity,
    /** Member path only — `undefined` for a guest, who has no plan to overrun. */
    overage: isAuthenticated ? member.data?.overage : undefined,
    /** Member path only. */
    usage: isAuthenticated ? member.data?.usage : undefined,
    /** Member path only — the guest payload carries no plan summary by design. */
    subscription: isAuthenticated ? member.data?.subscription : undefined,
    advanceBookingDays: data?.advanceBookingDays,
    /**
     * The verdict on `promoCode`. `applied: false` is ordinary — the price
     * shown is the list price and the booking can still be made; render
     * `message`, which the server authors.
     */
    promotion: data?.promotion,
    /**
     * `at_club` when the club collects what the booker owes at the counter — book with
     * no card and say "Due at Club". Absent on a refusal.
     */
    paymentTiming: data?.paymentTiming,
    /**
     * Member path only — the booker's account credit against what they owe now, and the
     * server's card verdict (credits D9, D29). A guest quote carries neither.
     */
    credit: isAuthenticated ? member.data?.credit : undefined,
    paymentMethodRequired: isAuthenticated ? member.data?.paymentMethodRequired : undefined,

    isLoading: active.isLoading,
    isError: active.isError,
    refetch: active.refetch,
    /**
     * Exposed because "disabled" is a quote *state*, not an implementation
     * detail: React Query reports `isLoading: false` with no data while disabled,
     * which is indistinguishable from "answered" unless the caller can see this.
     */
    enabled: isEnabled,
    /** True once this booker has a server price in hand. */
    hasQuote: !!data,
  };
}
